/**
 * CalendarPage — Phase 4 main page.
 *
 * Replaces the mock-data ``Calendar.tsx`` that lived alongside this file.
 * Renders a week grid keyed by data-testid (no third-party calendar lib
 * — keeps E2E selectors stable, no fixture wrangling for FullCalendar).
 *
 * Timezone discipline (Phase-4 review fix)
 * ----------------------------------------
 * EVERY user-visible date computation runs in the tenant's timezone. The
 * week anchor, the day-column headers, the cell keys, and the event-bucket
 * keys all derive from the *same* tenant-tz wall-clock dates. Mixing the
 * browser's local tz with the tenant tz (the original bug) made chips
 * silently disappear when the operator's browser tz ≠ tenant tz.
 *
 * Drag-to-reschedule:
 *   1. Drop a session onto a new slot → optimistic update (in the
 *      ``useUpdateSession`` mutation's ``onMutate``).
 *   2. Pre-flight ``POST /v1/calendar/conflicts/check``.
 *   3. If no conflicts → ``PATCH /sessions/{id}`` with new starts/ends.
 *   4. If conflicts → open ``ConflictWarningModal``; on Override resubmit
 *      with ``override_conflicts: true``.
 *   5. On API error / Back → rollback handled by the mutation's
 *      ``onError`` using the snapshot stored in ``onMutate``.
 */

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Button, Card } from '@/components/common';
import { PageContainer } from '@/components/layout';
import { useAuth } from '@/context';
import { ApiError } from '@core/api/client';
import {
  useSessions,
  useUpdateSession,
  type SessionResponse,
} from '@features/calendar/api/sessions';
import { useBlocks, type CalendarBlockResponse } from '@features/calendar/api/blocks';
import { useBranches } from '@features/calendar/api/branches';
import { useSubjectOptions, useTeacherOptions } from '@features/calendar/api/lookups';
import { ClassScheduleModal } from '@features/calendar/components/ClassScheduleModal';
import { ConflictWarningModal } from '@features/calendar/components/ConflictWarningModal';
import { DayView } from '@features/calendar/components/DayView';
import { MonthView } from '@features/calendar/components/MonthView';
import { SessionChip } from '@features/calendar/components/SessionChip';
import { CALENDAR_UI } from '@features/calendar/config/ui';
import {
  addDays,
  addMonths,
  fmtIsoDate,
  inTenantTz,
  monthGridDates,
  sameWallClockDay,
  startOfIsoWeekInTz,
  startOfMonthInTz,
  tenantWallClockToUtc,
} from '@features/calendar/utils/tz';
import type { ConflictItem } from '@features/calendar/api/conflicts';
import { checkConflicts } from '@features/calendar/api/conflicts';
import { ImportExportButtons } from '@features/imports';

/** Which grid the operator is looking at. */
type CalendarView = 'month' | 'week' | 'day';

const FILTER_STYLE: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 13,
  background: 'white',
  minWidth: 140,
};

interface PendingReschedule {
  sessionId: string;
  newStartsAt: string; // ISO UTC
  newEndsAt: string;
  teacherId: string;
  roomId: string | null;
  conflicts: ConflictItem[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const tenantTz = user?.tenant_timezone ?? 'UTC';

  // ``anchorDate`` is a wall-clock Date *in tenant tz* representing the
  // Monday of the displayed ISO week. We never compare it against
  // ``new Date()`` directly.
  const [anchorDate, setAnchorDate] = useState(() =>
    startOfIsoWeekInTz(inTenantTz(new Date(), tenantTz)),
  );
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  // 11E.7: clicking a chip now opens the full session page (`/sessions/:id`)
  // rather than the Phase-4 drawer — the Register, Actions menu and left
  // rail need more room than a drawer affords. `SessionDetailDrawer` is
  // retained in the module for the portal/read-only surfaces that still
  // use it.
  const [pendingReschedule, setPendingReschedule] =
    useState<PendingReschedule | null>(null);
  const [view, setView] = useState<CalendarView>('week');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterBranch, setFilterBranch] = useState('');

  const routerNavigate = useNavigate();

  // Build the 7 tenant-tz wall-clock dates for the visible week.
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(anchorDate, i)),
    [anchorDate],
  );

  /** The 42-cell month grid, anchored on the month containing `anchorDate`. */
  const gridDates = useMemo(
    () => monthGridDates(startOfMonthInTz(anchorDate)),
    [anchorDate],
  );

  /**
   * The tenant-tz wall-clock window each view needs, converted to UTC for
   * the API. Month must span the whole 42-cell grid (not just the calendar
   * month) or the leading/trailing cells render empty.
   */
  const { fromIso, toIso } = useMemo(() => {
    const toUtcMidnight = (d: Date) =>
      tenantWallClockToUtc(d.getFullYear(), d.getMonth(), d.getDate(), 0, tenantTz);

    let start: Date;
    let endExclusive: Date;
    if (view === 'month') {
      start = gridDates[0];
      endExclusive = addDays(gridDates[gridDates.length - 1], 1);
    } else if (view === 'day') {
      start = anchorDate;
      endExclusive = addDays(anchorDate, 1);
    } else {
      start = weekDates[0];
      endExclusive = addDays(weekDates[6], 1);
    }
    return {
      fromIso: toUtcMidnight(start).toISOString(),
      toIso: toUtcMidnight(endExclusive).toISOString(),
    };
  }, [view, anchorDate, weekDates, gridDates, tenantTz]);

  const sessionsParams = {
    from: fromIso,
    to: toIso,
    teacher_id: filterTeacher || undefined,
    subject_id: filterSubject || undefined,
    branch_id: filterBranch || undefined,
  };
  const { data: sessions, isLoading, isError, refetch } = useSessions(sessionsParams);
  const updateMut = useUpdateSession(sessionsParams);

  const { data: blocks } = useBlocks({
    from: fromIso,
    to: toIso,
    teacher_id: filterTeacher || undefined,
  });

  const { data: teacherOptions } = useTeacherOptions();
  const { data: subjectOptions } = useSubjectOptions();
  const { data: branchesResult } = useBranches('active');
  const branchOptions = useMemo(
    () => (branchesResult?.items ?? []).map((b) => ({ value: b.id, label: b.name })),
    [branchesResult],
  );

  /** Header label — each view names its own span. */
  const rangeLabel = useMemo(() => {
    if (view === 'month') {
      return anchorDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (view === 'day') {
      return anchorDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    const start = weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = weekDates[6].toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${start} – ${end}`;
  }, [view, anchorDate, weekDates]);

  /** subject_id → name, for chip labels. */
  const subjectNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of subjectOptions ?? []) m[o.value] = o.label;
    return m;
  }, [subjectOptions]);

  const sessionsByDayHour = useMemo(() => {
    const map = new Map<string, SessionResponse[]>();
    for (const s of sessions ?? []) {
      // Project the UTC instant into the tenant tz and bucket by
      // wall-clock date + hour — matches the cell key below.
      const d = inTenantTz(new Date(s.starts_at), tenantTz);
      const key = `${fmtIsoDate(d)}_${d.getHours()}`;
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return map;
  }, [sessions, tenantTz]);

  /** Day-granularity bucket for the month grid. */
  const sessionsByDay = useMemo(() => {
    const map = new Map<string, SessionResponse[]>();
    for (const s of sessions ?? []) {
      const key = fmtIsoDate(inTenantTz(new Date(s.starts_at), tenantTz));
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    // Chips within a day read top-to-bottom in start order.
    for (const arr of map.values()) {
      arr.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    }
    return map;
  }, [sessions, tenantTz]);

  /**
   * Blocks bucketed onto every day they overlap — a multi-day holiday must
   * appear on each of its days, not only the one it starts on.
   */
  const blocksByDay = useMemo(() => {
    const map = new Map<string, CalendarBlockResponse[]>();
    for (const b of blocks ?? []) {
      let cursor = inTenantTz(new Date(b.starts_at), tenantTz);
      const end = inTenantTz(new Date(b.ends_at), tenantTz);
      // Walk wall-clock days from start to end inclusive. Bounded by the
      // query window, so this cannot run away on a bad record.
      for (let guard = 0; cursor <= end && guard < 400; guard += 1) {
        const key = fmtIsoDate(cursor);
        const arr = map.get(key) ?? [];
        arr.push(b);
        map.set(key, arr);
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [blocks, tenantTz]);

  const todayInTz = useMemo(() => inTenantTz(new Date(), tenantTz), [tenantTz]);

  const navigate = (direction: -1 | 1) => {
    if (view === 'month') {
      setAnchorDate((d) => addMonths(d, direction));
    } else if (view === 'day') {
      setAnchorDate((d) => addDays(d, direction));
    } else {
      setAnchorDate((d) => addDays(d, 7 * direction));
    }
  };

  const goToday = () => {
    const nowTz = inTenantTz(new Date(), tenantTz);
    setAnchorDate(view === 'week' ? startOfIsoWeekInTz(nowTz) : nowTz);
  };

  /** Switching view re-anchors so the new grid contains the same day. */
  const switchView = (next: CalendarView) => {
    setView(next);
    setAnchorDate((d) => (next === 'week' ? startOfIsoWeekInTz(d) : d));
  };

  const openSession = (s: SessionResponse) => routerNavigate(`/sessions/${s.id}`);

  // -------------------------------------------------------------------------
  // Drag-and-drop reschedule
  // -------------------------------------------------------------------------

  const performUpdate = async (
    sessionId: string,
    body: {
      starts_at: string;
      ends_at: string;
      override_conflicts: boolean;
    },
  ) => {
    try {
      await updateMut.mutateAsync({ id: sessionId, body });
      toast.success('Session rescheduled');
      return true;
    } catch (e) {
      const isConflict =
        e instanceof ApiError && e.code === 'session_has_conflict';
      if (!isConflict) {
        toast.error(e instanceof ApiError ? e.message : 'Reschedule failed');
      }
      return false;
    }
  };

  /** ``newDate`` is a tenant-tz wall-clock Date for the dropped-on column. */
  const handleDrop = async (sessionId: string, newDate: Date, newHour: number) => {
    const original = (sessions ?? []).find((s) => s.id === sessionId);
    if (!original) return;

    const origStart = new Date(original.starts_at);
    const origEnd = new Date(original.ends_at);
    const durationMs = origEnd.getTime() - origStart.getTime();

    // Preserve the original minute offset within the hour, but anchor the
    // wall-clock to the tenant tz of the dropped slot.
    const origStartInTz = inTenantTz(origStart, tenantTz);
    const newStartUtc = tenantWallClockToUtc(
      newDate.getFullYear(),
      newDate.getMonth(),
      newDate.getDate(),
      newHour,
      tenantTz,
    );
    const newStart = new Date(
      newStartUtc.getTime() + origStartInTz.getMinutes() * 60 * 1000,
    );
    const newEnd = new Date(newStart.getTime() + durationMs);

    if (newStart.getTime() === origStart.getTime()) return;

    // Pre-flight conflict check — keep the user out of optimistic-rollback
    // hell when we can predict the failure before mutating.
    let conflictResult;
    try {
      conflictResult = await checkConflicts({
        starts_at: newStart.toISOString(),
        ends_at: newEnd.toISOString(),
        teacher_id: original.teacher_id,
        room_id: original.room_id ?? null,
        exclude_session_id: sessionId,
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Conflict check failed');
      return;
    }

    if (conflictResult.has_conflicts) {
      setPendingReschedule({
        sessionId,
        newStartsAt: newStart.toISOString(),
        newEndsAt: newEnd.toISOString(),
        teacherId: original.teacher_id,
        roomId: original.room_id ?? null,
        conflicts: conflictResult.conflicts,
      });
      return;
    }

    await performUpdate(sessionId, {
      starts_at: newStart.toISOString(),
      ends_at: newEnd.toISOString(),
      override_conflicts: false,
    });
  };

  const handleOverride = async () => {
    if (!pendingReschedule) return;
    await performUpdate(pendingReschedule.sessionId, {
      starts_at: pendingReschedule.newStartsAt,
      ends_at: pendingReschedule.newEndsAt,
      override_conflicts: true,
    });
    setPendingReschedule(null);
  };

  const handleConflictBack = () => {
    // No rollback needed — the optimistic update happens inside the mutation,
    // and we never invoked it for this code path.
    setPendingReschedule(null);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <PageContainer
      title="Calendar"
      subtitle="Schedule and manage classes"
      actions={
        <>
          <ImportExportButtons entity="sessions" entityLabel="Sessions" />
          <Button
            data-testid="candeza-calendar-new-class-btn"
            onClick={() => setIsClassModalOpen(true)}
          >
            + New class
          </Button>
        </>
      }
    >
      <Card padding="lg" data-testid="candeza-calendar-page">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Button
              size="sm"
              variant="outline"
              data-testid="candeza-calendar-prev-btn"
              onClick={() => navigate(-1)}
            >
              ‹
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="candeza-calendar-today-btn"
              onClick={goToday}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="candeza-calendar-next-btn"
              onClick={() => navigate(1)}
            >
              ›
            </Button>
            <strong
              data-testid="candeza-calendar-range-label"
              style={{ marginLeft: 12 }}
            >
              {rangeLabel}
            </strong>
          </div>

          <div
            role="group"
            aria-label="Calendar view"
            style={{ display: 'flex', gap: 4 }}
          >
            {(['month', 'week', 'day'] as const).map((v) => (
              <Button
                key={v}
                size="sm"
                variant={view === v ? 'primary' : 'outline'}
                data-testid={`candeza-calendar-view-${v}`}
                aria-pressed={view === v}
                onClick={() => switchView(v)}
              >
                {v[0].toUpperCase() + v.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        <div
          data-testid="candeza-calendar-filters"
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 16,
            alignItems: 'center',
          }}
        >
          <select
            data-testid="candeza-calendar-filter-staff"
            aria-label="Filter by staff"
            value={filterTeacher}
            onChange={(e) => setFilterTeacher(e.target.value)}
            style={FILTER_STYLE}
          >
            <option value="">All staff</option>
            {(teacherOptions ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            data-testid="candeza-calendar-filter-subject"
            aria-label="Filter by subject"
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            style={FILTER_STYLE}
          >
            <option value="">All subjects</option>
            {(subjectOptions ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            data-testid="candeza-calendar-filter-location"
            aria-label="Filter by location"
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            style={FILTER_STYLE}
          >
            <option value="">All locations</option>
            {(branchOptions ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {(filterTeacher || filterSubject || filterBranch) && (
            <Button
              size="sm"
              variant="outline"
              data-testid="candeza-calendar-filter-clear"
              onClick={() => {
                setFilterTeacher('');
                setFilterSubject('');
                setFilterBranch('');
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        {isLoading && (
          <div
            data-testid="candeza-calendar-loading"
            style={{ padding: 24, color: '#64748b' }}
          >
            Loading sessions…
          </div>
        )}
        {isError && (
          <div
            data-testid="candeza-calendar-error"
            style={{ padding: 24, color: '#dc2626' }}
          >
            Failed to load sessions.{' '}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && view === 'month' && (
          <MonthView
            gridDates={gridDates}
            currentMonth0={anchorDate.getMonth()}
            todayInTz={todayInTz}
            tenantTz={tenantTz}
            sessionsByDay={sessionsByDay}
            blocksByDay={blocksByDay}
            subjectNames={subjectNames}
            onSessionClick={openSession}
            onShowMore={(d) => {
              setAnchorDate(d);
              setView('day');
            }}
          />
        )}

        {!isLoading && !isError && view === 'day' && (
          <DayView
            date={anchorDate}
            tenantTz={tenantTz}
            sessionsByDayHour={sessionsByDayHour}
            blocks={blocksByDay.get(fmtIsoDate(anchorDate)) ?? []}
            subjectNames={subjectNames}
            onSessionClick={openSession}
            onDropSession={(sid, d, hour) => void handleDrop(sid, d, hour)}
          />
        )}

        {!isLoading && !isError && view === 'week' && (
          <div
            data-testid="candeza-calendar-week-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '60px repeat(7, 1fr)',
              gap: 1,
              background: '#e2e8f0',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <div style={{ background: '#f8fafc' }} />
            {weekDates.map((d, idx) => (
              <div
                key={idx}
                data-testid={`candeza-calendar-day-header-${fmtIsoDate(d)}`}
                style={{
                  background: '#f8fafc',
                  padding: 8,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <div style={{ color: '#64748b' }}>
                  {CALENDAR_UI.WEEKDAY_LABELS[idx]}
                </div>
                <div style={{ fontSize: 16 }}>{d.getDate()}</div>
              </div>
            ))}

            {CALENDAR_UI.TIME_SLOTS.map((time) => {
              const hour = parseInt(time.split(':')[0], 10);
              return (
                <React.Fragment key={time}>
                  <div
                    style={{
                      background: 'white',
                      padding: 6,
                      fontSize: 11,
                      color: '#64748b',
                      textAlign: 'right',
                    }}
                  >
                    {time}
                  </div>
                  {weekDates.map((d) => {
                    const cellKey = `${fmtIsoDate(d)}_${hour}`;
                    const events = sessionsByDayHour.get(cellKey) ?? [];
                    return (
                      <div
                        key={cellKey}
                        data-testid={`candeza-calendar-slot-${fmtIsoDate(d)}-${time}`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const sid = e.dataTransfer.getData('text/session-id');
                          if (sid) {
                            void handleDrop(sid, d, hour);
                          }
                        }}
                        style={{
                          background: sameWallClockDay(d, todayInTz)
                            ? '#eef2ff'
                            : 'white',
                          minHeight: 48,
                          padding: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {events.map((s) => (
                          <SessionChip
                            key={s.id}
                            session={s}
                            tenantTz={tenantTz}
                            subjectNames={subjectNames}
                            density="full"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSession(s);
                            }}
                          />
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {!isLoading && !isError && (sessions?.length ?? 0) === 0 && (
          <div
            data-testid="candeza-calendar-empty"
            style={{ marginTop: 16, color: '#64748b', textAlign: 'center' }}
          >
            No sessions scheduled in this week.
          </div>
        )}
      </Card>

      <ClassScheduleModal
        isOpen={isClassModalOpen}
        onClose={() => setIsClassModalOpen(false)}
      />

      {pendingReschedule && (
        <ConflictWarningModal
          isOpen
          onClose={handleConflictBack}
          onOverride={handleOverride}
          conflicts={pendingReschedule.conflicts}
          busy={updateMut.isPending}
        />
      )}

    </PageContainer>
  );
};
